"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script"; // ✅ Import Script
import dynamic from "next/dynamic";
import "./globals.css";
import Header from "@/components/common/Header";
const Footer = dynamic(() => import("@/components/common/Footer"), {
  ssr: true, // Keep SSR for SEO since it's the footer
});
import { QuoteProvider } from "@/contexts/QuoteContext";
import { ToastProvider } from "@/contexts/ToastContext";

const ScrollToTop = dynamic(() => import("@/components/common/ScrollToTop"), {
  ssr: false,
});
const ToastContainer = dynamic(() => import("@/components/common/ToastContainer"), {
  ssr: false,
});
const FloatingBlogButton = dynamic(
  () => import("@/components/common/FloatingBlogButton"),
  { ssr: false }
);

export default function RootLayout({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [queryClient] = useState(() => new QueryClient());
  const pathname = usePathname();

  const shouldHideHeaderFooter =
    pathname?.startsWith("/product-category/products") ||
    pathname?.startsWith("/product-category/categories");

  return (
    <html lang="en">
      <head>
        {/* Preload critical fonts to shorten critical path (avoids ~1.4s chain: HTML → CSS → fonts) */}
        <link
          rel="preload"
          href="/fonts/Satoshi-Regular.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/Satoshi-Medium.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/Satoshi-Bold.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="image"
          href="/images/WinnersPopupNew.webp"
        />
        <Script id="google-consent-default" strategy="beforeInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', {
              ad_storage: 'denied',
              ad_user_data: 'denied',
              ad_personalization: 'denied',
              analytics_storage: 'denied',
              functionality_storage: 'granted',
              security_storage: 'granted',
              wait_for_update: 500
            });
          `}
        </Script>
        {/* Google Tag Manager */}
        <Script id="google-tag-manager" strategy="lazyOnload">
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-5RXQPR7X');
          `}
        </Script>
        {/*  End Google Tag Manager - afterInteractive helps back/forward cache */}

        {/* <title>Gemini UAE</title> */}
        {/* <meta
          name="description"
          content="Gemini Building Materials: Largest stockist drywall systems, Gypsum and building materials in UAE."
        /> */}

        {/* ✅ Google Site Verification */}
        <meta
          name="google-site-verification"
          content="VqVyJpYM_MTC8oPc8Z-mnsYydOlXEodE1hLVoTBtS1o"
        />

        {/* Website Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Gemini Building Materials LLC",
              legalName: "Gemini Building Materials LLC",
              url: "https://geminiuae.com/",
              logo: "https://geminiuae.com/assets/logo.png",
              description:
                "Leading importer and distributor of building materials in the UAE, trading a wide range of products including gypsum boards, ceilings, partitions and construction supplies since 1995.",
              foundingDate: "1995",
              founders: [],
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  telephone: "+97126740505",
                  contactType: "customer service",
                  areaServed: "AE",
                  availableLanguage: "en",
                },
              ],
              address: {
                "@type": "PostalAddress",
                streetAddress:
                  "14th Floor, Bin Ghanim Tower, Hamden Street, Tourist Club Area",
                addressLocality: "Abu Dhabi",
                postalCode: "71295",
                addressCountry: "AE",
              },
              sameAs: [
                "https://www.linkedin.com/company/gemini-building-materials",
                "https://www.facebook.com/geminigroup.gbm",
              ],
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Building Materials Products and Services",
                itemListElement: [],
              },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: "Gemini Building Materials LLC",
              image: "https://geminiuae.com/assets/logo.png",
              description:
                "Supplier and distributor of ceiling solutions, gypsum boards, partitions, and building materials in the UAE and GCC.",
              telephone: "+97126740505",
              address: {
                "@type": "PostalAddress",
                streetAddress:
                  "14th Floor, Bin Ghanim Tower, Hamden Street, Tourist Club Area",
                addressLocality: "Abu Dhabi",
                addressRegion: "Abu Dhabi",
                postalCode: "71295",
                addressCountry: "AE",
              },
              geo: {
                "@type": "GeoCoordinates",
                latitude: 24.453884,
                longitude: 54.377343,
              },
              url: "https://geminiuae.com/",
              openingHours: "Mo,Tu,We,Th,Fr 08:00-18:00",
              priceRange: "$$",
              sameAs: [
                "https://www.linkedin.com/company/gemini-building-materials",
                "https://www.facebook.com/geminigroup.gbm",
              ],
            }),
          }}
        />




        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What building materials are supplied by Gemini Building Materials LLC in the UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Gemini Building Materials LLC provides a broad range of building materials for civil construction and related technologies, including industrial-grade supplies for residential, commercial, and infrastructure construction projects across the UAE."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials LLC a worthwhile supplier of materials in Abu Dhabi?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials LLC is a reputable building materials supplier in Abu Dhabi, recognised for high-quality products, affordable pricing, and reliable delivery."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is there an exact location of Gemini Building Materials in Mussafah?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials is located in Mussafah, Abu Dhabi, a major industrial area that allows contractors and builders to access building supplies quickly."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do the same building materials used by Gemini Building Materials fit my area of Abu Dhabi?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials serves Abu Dhabi and surrounding regions with building materials suitable for local construction requirements, supported by quick delivery."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does Gemini Building Materials have a presence in Dubai and Sharjah?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials distributes building materials across Dubai, Sharjah, and other Emirates through a well-established UAE distribution network."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Which kinds of building materials are available at Gemini UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Structural materials, finishing materials, technical construction products, and essential building supplies for small- and large-scale construction projects are available at Gemini UAE."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does Gemini Building Materials LLC have the capability of large construction works?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials LLC consistently supplies materials for large-scale commercial, industrial, and infrastructure construction projects in the UAE."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Why Gemini Building Materials among building material firms in UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Gemini Building Materials stands out for stable supply, high-quality products, timely restocking, reasonable pricing, and close technical support to contractors and developers."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials in compliance with urgent material needs?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials supports urgent and quantity-based material requirements through efficient logistics and material coordination."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do Gemini Building Materials products meet UAE requirements?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, all building materials supplied by Gemini Building Materials comply with UAE quality and safety standards."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Are contractors selecting building materials in Mussafah through Gemini?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, many contractors in Mussafah source materials directly from Gemini Building Materials to enable faster procurement and reduce project downtime."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials in support of custom requirements?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials assists in sourcing specific building materials based on project technical specifications."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do Gemini Building Materials accommodate small and large orders?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials serves both small quantity buyers and large-scale construction projects."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What makes Gemini Building Materials a favourable building materials organisation in the UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Gemini Building Materials is preferred for its strong supply chain, high-quality products, customer-focused approach, and UAE-wide distribution."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials operating in all the Emirates?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials distributes building materials across Abu Dhabi, Dubai, Sharjah, and other Emirates in the UAE."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do building supplies in the Mussafah area have pick up options?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, many building materials can be collected in bulk from the Mussafah location or dispatched promptly."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials a single-stop supplier of building materials?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials operates as a one-stop building materials supplier, eliminating the need to coordinate with multiple vendors."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the contribution of Gemini Building Materials to construction delays reduction?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Gemini Building Materials helps reduce construction delays by maintaining ready stock, ensuring reliable sourcing, and delivering materials quickly."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does Gemini Building Materials provide technical construction materials?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials provides specialised technical construction materials suitable for demanding engineering and construction conditions."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials LLC registered in the UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials LLC is a registered and licensed building materials company in the UAE."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is it possible to acquire bulk prices of building materials at Gemini UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials offers bulk pricing and long-term supply rates based on project size and material quantity."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does Gemini Building Materials target industrial projects?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials supplies materials to industrial facilities, warehouses, factories, and infrastructure projects."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the contact information for Gemini Building Materials?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "You can contact Gemini Building Materials through their official website or directly reach their sales team for customised quotations."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Would Gemini Building Materials be convenient to contractors and developers?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials works closely with contractors, developers, consultants, and project managers across the UAE."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does Gemini Building Materials distribute building materials in the Emirate of Abu Dhabi?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, in addition to Mussafah, Gemini Building Materials distributes building materials across Abu Dhabi and surrounding regions."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Why can Gemini Building Materials be considered a reliable partner in the UAE?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Gemini Building Materials is considered reliable due to its industry experience, consistent material quality, professional service, and strong client relationships."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials able to sustain long-term construction projects?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials supports long-term construction projects with stable supply planning and consistent material availability."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials offering building materials to commercial buildings?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials supplies materials suitable for offices, malls, commercial complexes, and mixed-use developments."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Will Gemini Building Materials be a good substitute to Perfect Building Materials Trading LLC?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, many contractors prefer Gemini Building Materials due to reliable stock availability, consistent quality, and responsive customer service."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Gemini Building Materials selling to residential construction projects?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Gemini Building Materials supplies villas, apartments, and residential developments across Abu Dhabi and the UAE."
                  }
                }
              ]
            })
          }}
        />
      </head>
      <body className="antialiased pb-[80px] lg:pb-0">
        {/* Google Tag Manager (noscript) - Injected via Script */}
        {/* <noscript>
          <iframe 
            src="https://www.googletagmanager.com/ns.html?id=GTM-5RXQPR7X"
            height="0" width="0" 
            style={{ display: "none", visibility: "hidden" }}>
          </iframe>
        </noscript> */}
        <Script id="gtm-noscript" strategy="lazyOnload">
          {`
            (function() {
              var noscript = document.createElement('noscript');
              var iframe = document.createElement('iframe');
              iframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-5RXQPR7X';
              iframe.height = '0';
              iframe.width = '0';
              iframe.style.display = 'none';
              iframe.style.visibility = 'hidden';
              noscript.appendChild(iframe);
              document.body.insertBefore(noscript, document.body.firstChild);
            })();
          `}
        </Script>
        {/* End Google Tag Manager (noscript) */}
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <QuoteProvider>
              <div
                className={`${shouldHideHeaderFooter ? "lg:block hidden" : "block"
                  }`}
              >
                <Header />
              </div>
              <div className="max-sm:hidden">
                <FloatingBlogButton />
              </div>
              {children}
              <div
                className={`${shouldHideHeaderFooter ? "lg:block hidden" : "block"
                  }`}
              >
                <Footer />
              </div>
              <ScrollToTop />
              <ToastContainer />
            </QuoteProvider>
          </ToastProvider>
        </QueryClientProvider>
      </body>
    </html>
  );
}
